import axios from "axios";

// Base API - meme convention que le CRM CB Courtage (backend sur :8000).
const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const api = axios.create({ baseURL: API_URL });

// Le jeton ne sort JAMAIS vers un tiers.
//
// L'intercepteur posait l'en-tete Authorization sur toutes les requetes
// passant par cette instance. Tant que l'application n'appelle qu'elle-meme,
// cela ne se voit pas ; le jour ou un ecran interroge un service exterieur
// (une API publique d'immatriculation, une carte, un geocodeur), le jeton
// part avec -- et il ouvre tout le compte.
//
// Deuxieme effet, plus vicieux parce qu'il est silencieux : un en-tete
// Authorization rend la requete « non simple » au sens CORS. Le navigateur
// demande alors une autorisation prealable, le service tiers la refuse, et
// l'appel echoue sans erreur lisible. C'est exactement ce qui a coute deux
// jours sur le CRM CB Courtage avec l'API entreprises de l'Etat.
//
// On ne le pose donc que sur les URL relatives et sur celles de notre API.
function versNotreApi(url) {
  if (!url) return true;                       // requete sans URL : relative
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).origin === new URL(API_URL, window.location.origin).origin;
    } catch {
      return false;                            // URL illisible : dans le doute, non
    }
  }
  if (url.startsWith("//")) return false;       // //exemple.fr/... : absolue deguisee
  return true;                                  // /vehicules, vehicules/12...
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && versNotreApi(config.url)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { versNotreApi };

/**
 * Le message a afficher pour une erreur d'API.
 *
 * FastAPI met un texte dans `detail` quand la route refuse (409, 400...), et
 * une LISTE d'objets quand c'est Pydantic qui refuse (422). Lire seulement le
 * premier cas fait tomber les erreurs de validation sur un message generique,
 * alors que le serveur avait dit precisement ce qui n'allait pas.
 *
 * Cette fonction existait, recopiee a l'identique, dans cinq composants. Elle
 * vit ici desormais : api.js est la couche qui parle au serveur, c'est a elle
 * de savoir lire ses refus. (Les quatre autres copies restent en place pour
 * l'instant -- les remplacer sort du perimetre de cette tache.)
 */
export function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

// Session expiree : un 401 sur une requete DEJA authentifiee (token invalide /
// expire) deconnecte et renvoie vers le login avec un message clair. On exclut
// la requete /login elle-meme (mauvais identifiants) pour ne pas boucler.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || "";
    const surLogin = url.includes("/login");
    if (error?.response?.status === 401 && !surLogin) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login") {
        window.location.assign("/login?expired=1");
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Telecharge un fichier servi par une route AUTHENTIFIEE.
 *
 * Un simple <a href> ne convient pas : le navigateur n'y joint pas le jeton,
 * et la route repondrait 401. On recupere donc le contenu avec l'instance
 * axios (qui porte le jeton), puis on declenche l'enregistrement.
 *
 * L'URL temporaire est REVOQUEE juste apres. Sans cela, le contenu -- ici
 * l'integralite des donnees d'un client -- resterait accessible en memoire du
 * navigateur pour toute la duree de l'onglet.
 */
export async function telecharger(url, nomParDefaut) {
  const reponse = await api.get(url, { responseType: "blob" });

  // Le serveur donne le nom dans Content-Disposition ; on le respecte plutot
  // que d'en inventer un, pour que le fichier porte le nom du client.
  //
  // DEUX FORMES, ET `filename*` D'ABORD. Un en-tete HTTP se serialise en
  // latin-1 : le serveur envoie donc un repli ASCII sans accents dans
  // `filename`, et le VRAI nom percent-encode dans `filename*` (RFC 6266).
  // Lire `filename` en premier ferait tomber « Attestation sécu.pdf » en
  // « Attestation secu.pdf » alors que le bon nom est juste a cote.
  const entete = reponse.headers["content-disposition"] || "";
  const etoile = /filename\*=UTF-8''([^;]+)/i.exec(entete);
  const simple = /filename="?([^";]+)"?/.exec(entete);
  let nom = nomParDefaut;
  if (etoile) {
    try {
      nom = decodeURIComponent(etoile[1]);
    } catch {
      // Encodage abime : le repli ASCII vaut mieux qu'un nom inventé.
      nom = simple ? simple[1] : nomParDefaut;
    }
  } else if (simple) {
    nom = simple[1];
  }

  const lien = document.createElement("a");
  lien.href = window.URL.createObjectURL(reponse.data);
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  window.URL.revokeObjectURL(lien.href);
  return nom;
}

/** Taille de page demandée en parcourant une liste. C'est le plafond accepté
 *  par `GET /vehicules` (`page_size: int = Query(20, ge=1, le=500)`) : le
 *  dépasser ferait répondre 422, et le réduire multiplierait les allers-retours
 *  sans rien gagner. */
const PAGE_MAX = 500;

/**
 * TOUS les véhicules du client, en autant de requêtes qu'il en faut.
 *
 * POURQUOI CETTE FONCTION EXISTE
 *
 * Deux écrans (inspections, financement) remplissent un sélecteur avec la
 * flotte entière, et demandaient « 500 » en se fiant au commentaire « 500
 * couvre largement toute flotte réaliste ». 500 EST le plafond de la route :
 * au 501e camion, le sélecteur en proposait 500 et n'en disait rien. Le
 * mécanicien cherchant son véhicule ne trouvait pas de message d'erreur — il
 * trouvait une liste où son camion n'était pas, ce qui se lit comme « ce
 * camion n'existe pas » et non comme « l'écran est incomplet ».
 *
 * Un écran tronqué en silence est pire qu'un écran en panne : il se croit
 * juste, et l'utilisateur le croit aussi.
 *
 * La boucle s'arrête sur `nb_pages`, donné par le serveur — jamais sur « la
 * page est vide », qui tournerait sans fin si la réponse changeait de forme.
 * La borne dure de 40 pages (20 000 véhicules) existe pour la même raison :
 * une boucle réseau sans sortie de secours fige l'onglet.
 */
export async function chargerTousLesVehicules(params = {}) {
  const premiere = await api.get("/vehicules", {
    params: { ...params, page: 1, page_size: PAGE_MAX },
  });
  const items = premiere.data.items ?? [];
  const nbPages = Math.min(premiere.data.nb_pages ?? 1, 40);

  for (let page = 2; page <= nbPages; page += 1) {
    // En série et non en parallèle : une flotte de plusieurs milliers de
    // camions lancerait autant de requêtes simultanées sur un serveur qui en
    // sert d'autres. L'attente est celle d'un écran qui se remplit, pas celle
    // d'un utilisateur bloqué — le reste de la page s'affiche déjà.
    const suite = await api.get("/vehicules", {
      params: { ...params, page, page_size: PAGE_MAX },
    });
    items.push(...(suite.data.items ?? []));
  }
  return items;
}

export default api;
